import './style/mcpExampleChat.scss';

import React, {ReactNode} from 'react';
import {Trans, useTranslation} from 'react-i18next';

interface ChatTable {
    headers: Array<string>;
    rows: Array<Array<string>>;
}

interface ChatTurn {
    id: string;
    question: string;
    intro: ReactNode;
    notes?: Array<ReactNode>;
    table: ChatTable;
    followUp: string;
}

export default function McpExampleChat() {
    const {t} = useTranslation();

    const turns: Array<ChatTurn> = [
        {
            id: 'month',
            question: t('mcpServer.chat.monthQuestion', 'Hoe ging afgelopen maand?'),
            intro: (
                <Trans
                    i18nKey='mcpServer.chat.monthIntro'
                    defaults='September was een goede maand. Je omzet steeg met <0>12%</0> ten opzichte van augustus.'
                    components={[<strong key='0' />]}
                />
            ),
            table: {
                headers: [
                    '',
                    t('mcpServer.chat.monthColCurrent', 'September'),
                    t('mcpServer.chat.monthColPrevious', 'Augustus'),
                    t('mcpServer.chat.monthColDiff', 'Verschil')
                ],
                rows: [
                    [t('mcpServer.chat.monthRowRevenue', 'Omzet (incl. btw)'), '€54.120', '€48.320', '+12%'],
                    [t('mcpServer.chat.monthRowOrders', 'Orders'), '492', '455', '+8%'],
                    [t('mcpServer.chat.monthRowAverage', 'Gem. orderwaarde'), '€110', '€106', '+4%'],
                    [t('mcpServer.chat.monthRowAds', 'Ad spend'), '€3.240', '€3.020', '+7%'],
                    [t('mcpServer.chat.monthRowReturns', 'Retouren'), '18', '11', '+64%']
                ]
            },
            followUp: t(
                'mcpServer.chat.monthFollowUp',
                'Wil je dat ik de retourredenen van de braadpan verder uitzoek?'
            )
        },
        {
            id: 'returns',
            question: t('mcpServer.chat.returnsQuestion', 'Welke producten worden het vaakst geretourneerd?'),
            intro: t(
                'mcpServer.chat.returnsIntro',
                'Dit zijn je meest geretourneerde producten van de afgelopen 12 maanden:'
            ),
            table: {
                headers: [
                    t('mcpServer.chat.returnsColProduct', 'Product'),
                    t('mcpServer.chat.returnsColReturns', 'Retouren')
                ],
                rows: [
                    ['Gietijzeren braadpan 4,2 L – rood', '12'],
                    ['USB-C dockingstation – dual 4K', '8'],
                    ['Zwembadhoes 300 × 200 cm', '5']
                ]
            },
            followUp: t(
                'mcpServer.chat.returnsFollowUp',
                'De braadpan wordt het vaakst teruggestuurd. Wil je de retourredenen zien?'
            )
        },
        {
            id: 'seo',
            question: t('mcpServer.chat.seoQuestion', 'Hoe kan ik mijn product verbeteren voor SEO?'),
            intro: (
                <Trans
                    i18nKey='mcpServer.chat.seoIntro'
                    defaults='Je titel voor de <0>braadpan</0> is Engels en mist de zoekwoorden waar klanten op bol.com op zoeken.'
                    components={[<strong key='0' />]}
                />
            ),
            notes: [
                <Trans
                    key='current'
                    i18nKey='mcpServer.chat.seoCurrent'
                    defaults='<0>Nu:</0> Enamel Cast Iron Casserole 4.2L - Red'
                    components={[<strong key='0' />]}
                />,
                <Trans
                    key='better'
                    i18nKey='mcpServer.chat.seoBetter'
                    defaults='<0>Beter:</0> Gietijzeren braadpan 4,2 L – Dutch oven – Inductie – Rood'
                    components={[<strong key='0' />]}
                />
            ],
            table: {
                headers: [
                    t('mcpServer.chat.seoColKeyword', 'Belangrijkste zoekwoorden'),
                    t('mcpServer.chat.seoColVolume', 'Zoekvolume / mnd'),
                    t('mcpServer.chat.seoColInTitle', 'Nu in titel')
                ],
                rows: [
                    ['braadpan', '14.800', t('mcpServer.chat.no', 'Nee')],
                    ['dutch oven', '9.900', t('mcpServer.chat.no', 'Nee')],
                    ['gietijzeren braadpan', '6.600', t('mcpServer.chat.no', 'Nee')],
                    ['cast iron casserole', '90', t('mcpServer.chat.yes', 'Ja')]
                ]
            },
            followUp: t(
                'mcpServer.chat.seoFollowUp',
                'De nieuwe titel bevat de 3 grootste zoekwoorden, met "braadpan" vooraan. Wil je dat ik ook de beschrijving verbeter?'
            )
        }
    ];

    function renderTable(table: ChatTable) {
        return (
            <div className='chat-table-scroll'>
                <table className='chat-table'>
                    <thead>
                        <tr>
                            {table.headers.map((header, index) => (
                                <th key={index} className={index === 0 ? 'align-start' : 'align-end'}>
                                    {header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {table.rows.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                {row.map((cell, cellIndex) => (
                                    <td key={cellIndex} className={cellIndex === 0 ? 'align-start' : 'align-end'}>
                                        {cell}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    function renderTurn(turn: ChatTurn) {
        return (
            <div key={turn.id} className='chat-turn'>
                <div className='chat-question'>
                    <span className='chat-bubble'>{turn.question}</span>
                </div>
                <div className='chat-answer'>
                    <p className='chat-text'>{turn.intro}</p>
                    {turn.notes?.map((note, index) => (
                        <p key={index} className='chat-note'>
                            {note}
                        </p>
                    ))}
                    {renderTable(turn.table)}
                    <p className='chat-text'>{turn.followUp}</p>
                </div>
            </div>
        );
    }

    return <div className='mcp-example-chat'>{turns.map(renderTurn)}</div>;
}
